import type { ModeLimits } from '@/api/llmConfig';
import type { RunMode } from '@/api/runs';
import type { ComparisonSelectionTotals } from '@/types/chat/chat';
import type { ComparisonRequestCheck } from './comparisonRoles';
import { comparisonQuota } from './quota';

/**
 * Why Send is disabled (spec §3, table of blocking reasons), with the
 * resolution the composer offers. Derived from the `selection_totals` the
 * server resolves for the Q&A (the same resolution POST /llm/chat/runs
 * checks) and the limits of GET /llm/config. The server checks again at
 * submission.
 */

export type BlockCode =
  /** A run is active in this Q&A: Stop only. */
  | 'run_active'
  /** The selection is being resolved; wait for the result. */
  | 'calculating'
  /** The Q&A could not be loaded; Retry loads it again. */
  | 'load_failed'
  /** Attachments are still uploading. */
  | 'uploading'
  /** Selected documents are still processing. */
  | 'sources_pending'
  | 'no_usable_sources'
  | 'comparison_too_few'
  | 'comparison_over_limit'
  | 'baseline_missing'
  | 'document_to_check_missing';

export type BlockAction =
  | 'stop'
  | 'select_sources'
  | 'switch_chat'
  | 'send_ready_only'
  | 'show_breakdown'
  | 'choose_role'
  | 'retry_load';

export interface Block {
  code: BlockCode;
  actions: BlockAction[];
  /** Values for the message. */
  values: Record<string, number | string>;
  /**
   * Documents still uploading or processing when the reason is about the
   * ready ones (they are not counted yet).
   */
  pending: number;
}

export interface BlockInput {
  mode: RunMode;
  runActive: boolean;
  /**
   * The selected Q&A has loaded (its mode, selection and totals). Until
   * then nothing is sent, in any mode: the mode on screen may still be the
   * previous Q&A's.
   */
  inquiryReady: boolean;
  /** Loading the selected Q&A failed. */
  loadFailed: boolean;
  totals: ComparisonSelectionTotals | null | undefined;
  /** The selection changed and its totals are being read again. */
  calculating: boolean;
  /** Attachments chosen here that have not reached the server yet. */
  uploading: number;
  limits: ModeLimits['comparison'];
  /** The roles chosen for a comparison. */
  roles: ComparisonRequestCheck | null;
}

const block = (
  code: BlockCode,
  actions: BlockAction[],
  values: Block['values'] = {},
  pending = 0,
): Block => ({ code, actions, values, pending });

/** Conditions on the documents that are ready (usable) now. */
const readyReason = (input: BlockInput, totals: ComparisonSelectionTotals) => {
  if (Number(totals.file_count ?? 0) === 0) {
    return block('no_usable_sources', ['select_sources', 'switch_chat']);
  }
  if (input.mode !== 'comparison') return null;
  const quota = comparisonQuota(totals, input.limits);
  if (quota.tooFew) {
    return block('comparison_too_few', ['select_sources'], {
      min: quota.minFiles,
      n: quota.files,
    });
  }
  if (quota.over) {
    return block('comparison_over_limit', ['show_breakdown'], {
      n: quota.files,
      maxFiles: quota.maxFiles,
      mb: quota.mb,
      maxMb: quota.maxMb,
    });
  }
  if (input.roles && !input.roles.ok) {
    return block(
      input.roles.missing === 'baseline'
        ? 'baseline_missing'
        : 'document_to_check_missing',
      ['choose_role'],
    );
  }
  return null;
};

/** A refusal the server predicts that the rules above did not catch. */
const serverReason = (
  input: BlockInput,
  totals: ComparisonSelectionTotals,
): Block | null => {
  const code = totals.rejection_codes?.[input.mode];
  switch (code) {
    case 'no_usable_sources':
      return block('no_usable_sources', ['select_sources', 'switch_chat']);
    case 'comparison_too_few':
      return block('comparison_too_few', ['select_sources'], {
        min: input.limits.min_files,
        n: Number(totals.file_count ?? 0),
      });
    case 'comparison_over_limit': {
      const quota = comparisonQuota(totals, input.limits);
      return block('comparison_over_limit', ['show_breakdown'], {
        n: quota.files,
        maxFiles: quota.maxFiles,
        mb: quota.mb,
        maxMb: quota.maxMb,
      });
    }
    default:
      return null;
  }
};

/** The reason Send is disabled, or null when the question may be sent. */
export const blockingReason = (input: BlockInput): Block | null => {
  if (input.runActive) return block('run_active', ['stop']);
  if (!input.inquiryReady) {
    return input.loadFailed
      ? block('load_failed', ['retry_load'])
      : block('calculating', []);
  }
  if (input.mode === 'chat_only') return null;
  const totals = input.totals;
  if (input.calculating || !totals) return block('calculating', []);

  const pending = Number(totals.pending_count ?? 0) + input.uploading;
  const ready = readyReason(input, totals);
  if (pending > 0) {
    const code = input.uploading > 0 ? 'uploading' : 'sources_pending';
    // Nothing is ready yet: the only way is to wait.
    if (ready?.code === 'no_usable_sources') {
      return block(code, [], { count: pending }, pending);
    }
    // The ready documents could be sent as they are.
    if (!ready) {
      return block(code, ['send_ready_only'], { count: pending }, pending);
    }
    return { ...ready, pending };
  }
  return ready ?? serverReason(input, totals);
};

/** The message key of a reason (values from Block.values). */
export const BLOCK_MESSAGE_KEYS: Record<BlockCode, string> = {
  run_active: 'v2.composer.rRunActive',
  calculating: 'v2.composer.rCalculating',
  load_failed: 'v2.composer.rLoadFailed',
  uploading: 'v2.composer.rUploading',
  sources_pending: 'v2.composer.rProcessing',
  no_usable_sources: 'v2.composer.rNoSources',
  comparison_too_few: 'v2.composer.rCompareMin',
  comparison_over_limit: 'v2.composer.rOverLimit',
  baseline_missing: 'v2.composer.rBaseline',
  document_to_check_missing: 'v2.composer.rDocToCheck',
};
